<template>
  <el-drawer title="技师排班" size="60%" :visible.sync="open_modal">
    <div class="scheduling-container">
      <!-- 顶部技师信息 -->
      <div class="staff-info">
        <el-image
          style="width: 50px; height: 50px; border-radius: 50%"
          :src="staffInfo.thumb"
          :preview-src-list="[staffInfo.thumb]"
        />
        <div style="margin-left: 10px">
          <p><strong>{{ staffInfo.name }}</strong></p>
          <p>{{ staffInfo.phone }}</p>
        </div>
      </div>

      <!-- 日期选择 -->
      <div class="date-section">
        <h3>选择日期</h3>
        <div class="week-selector">
          <div 
            v-for="(day, index) in weekDays" 
            :key="index"
            :class="['day-item', { active: selectedDate === day.date }]"
            @click="selectDate(day.date)"
          >
            <div class="day-name">{{ day.name }}</div>
            <div class="day-number">{{ day.number }}</div>
          </div>
        </div>
      </div>

      <!-- 时间段选择 -->
      <div class="time-section">
        <h3>请选择接单时段</h3>
        <div class="time-slots">
          <div 
            v-for="slot in timeSlots" 
            :key="slot.time"
            :class="['time-slot', getSlotStatus(slot.time), { 'past-slot': isPastTimeSlot(slot.time) }]"
            @click="!isPastTimeSlot(slot.time) && toggleTimeSlot(slot.time)"
          >
            <div class="time">{{ slot.time }}</div>
            <div class="status">{{ getSlotStatusText(slot.time) }}</div>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="action-buttons">
        <el-button @click="closeAll">关闭全部</el-button>
        <el-button type="primary" @click="openAll">开启全部</el-button>
        <el-button type="warning" @click="copySetting">7天相同设置</el-button>
      </div>

      <!-- 底部保存按钮 -->
      <div class="save-section">
        <el-button type="primary" size="large" @click="saveSchedule" :loading="saving">保存</el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import { getStaffSchedule, saveStaffSchedule } from '@/api/technician';

export default {
  name: 'StaffScheduling',
  data() {
    return {
      open_modal: false,
      staffInfo: {},
      selectedDate: '',
      weekDays: [],
      timeSlots: [],
      scheduleData: {},
      saving: false
    };
  },
  methods: {
    // 打开排班弹窗
    openScheduling(staff) {
      this.staffInfo = staff;
      this.open_modal = true;
      this.initWeekDays();
      this.initTimeSlots();
      this.loadScheduleData();
    },

    // 将84字符排班字符串转换为界面显示
    parseScheduleString(scheduleString) {
      console.log('解析排班字符串:', scheduleString);
      
      // 验证字符串格式
      if (!scheduleString || scheduleString.length !== 84) {
        console.warn('排班字符串格式不正确，长度应为84，实际为:', scheduleString ? scheduleString.length : 0);
        return {};
      }
      
      let weekSchedule = {};
      
      try {
        for (let day = 0; day < 7; day++) {
          let daySchedule = {};
          let dayDate = this.weekDays[day].date;
          
          for (let slot = 0; slot < 12; slot++) {
            let charIndex = day * 12 + slot;
            let hexChar = scheduleString[charIndex];
            let bitMask = parseInt(hexChar, 16);
            
            if (isNaN(bitMask)) {
              console.warn(`无效的十六进制字符: ${hexChar} at index ${charIndex}`);
              bitMask = 0;
            }
            
            for (let halfHour = 0; halfHour < 4; halfHour++) {
              let hour = slot * 2 + Math.floor(halfHour / 2);
              let minute = (halfHour % 2) * 30;
              let timeKey = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
              let isAvailable = (bitMask & (1 << halfHour)) !== 0;
              daySchedule[timeKey] = isAvailable ? 1 : 0;
            }
          }
          weekSchedule[dayDate] = daySchedule;
        }
        
        console.log('解析后的排班数据:', weekSchedule);
        return weekSchedule;
      } catch (error) {
        console.error('解析排班字符串失败:', error);
        return {};
      }
    },

    // 将界面选择转换为84字符排班字符串
    convertToScheduleString(weekSchedule) {
      console.log('转换排班数据为字符串:', weekSchedule);
      console.log('weekDays:', this.weekDays);
      
      let result = '';
      
      try {
        // 如果没有排班数据，返回全0字符串
        if (!weekSchedule || Object.keys(weekSchedule).length === 0) {
          console.log('没有排班数据，返回全0字符串');
          return '0'.repeat(84);
        }
        
        for (let day = 0; day < 7; day++) {
          let dayDate = this.weekDays[day] ? this.weekDays[day].date : null;
          let daySchedule = dayDate ? (weekSchedule[dayDate] || {}) : {};
          
          console.log(`第${day}天 (${dayDate}):`, daySchedule);
          
          for (let slot = 0; slot < 12; slot++) {
            let bitMask = 0;
            
            for (let halfHour = 0; halfHour < 4; halfHour++) {
              let hour = slot * 2 + Math.floor(halfHour / 2);
              let minute = (halfHour % 2) * 30;
              let timeKey = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
              
              if (daySchedule[timeKey] === 1) {
                bitMask |= (1 << halfHour);
              }
            }
            result += bitMask.toString(16).toUpperCase();
          }
        }
        
        console.log('生成的排班字符串:', result, '长度:', result.length);
        
        // 验证结果长度并补齐
        if (result.length < 84) {
          console.warn('排班字符串长度不足，补齐到84位');
          result = result.padEnd(84, '0');
        } else if (result.length > 84) {
          console.warn('排班字符串长度超出，截取前84位');
          result = result.substring(0, 84);
        }
        
        return result;
      } catch (error) {
        console.error('转换排班数据失败:', error);
        return '0'.repeat(84); // 返回默认的84个0
      }
    },

    // 初始化周日期
    initWeekDays() {
      const today = new Date();
      const weekNames = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
      this.weekDays = [];
      
      // 从今天开始，显示今天+未来6天（共7天）
      for (let i = 0; i < 7; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        const dayOfWeek = date.getDay(); // 0=周日, 1=周一, ..., 6=周六
        
        this.weekDays.push({
          name: weekNames[dayOfWeek],
          number: date.getDate(),
          date: this.formatDate(date)
        });
      }
      
      // 默认选择今天
      this.selectedDate = this.formatDate(today);
    },

    // 初始化时间段
    initTimeSlots() {
      this.timeSlots = [];
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          this.timeSlots.push({ time });
        }
      }
    },

    // 选择日期
    selectDate(date) {
      this.selectedDate = date;
    },

    // 获取时间段状态
    getSlotStatus(time) {
      if (!this.scheduleData[this.selectedDate]) {
        return 'available';
      }
      const daySchedule = this.scheduleData[this.selectedDate];
      return daySchedule[time] === 1 ? 'available' : 'busy';
    },

    // 获取时间段状态文本
    getSlotStatusText(time) {
      if (this.isPastTimeSlot(time)) {
        return '时间已过';
      }
      return this.getSlotStatus(time) === 'available' ? '接单中' : '忙时';
    },

    // 判断时间段是否已过
    isPastTimeSlot(time) {
      const now = new Date();
      const currentDate = this.formatDate(now);
      
      // 如果不是今天，则不是过去时间
      if (this.selectedDate !== currentDate) {
        return false;
      }
      
      // 如果是今天，检查时间是否已过
      const [hours, minutes] = time.split(':').map(Number);
      const slotTime = new Date();
      slotTime.setHours(hours, minutes, 0, 0);
      
      return slotTime < now;
    },

    // 切换时间段状态
    toggleTimeSlot(time) {
      if (!this.scheduleData[this.selectedDate]) {
        this.$set(this.scheduleData, this.selectedDate, {});
      }
      const currentStatus = this.scheduleData[this.selectedDate][time] || 0;
      this.$set(this.scheduleData[this.selectedDate], time, currentStatus === 1 ? 0 : 1);
    },

    // 关闭全部时间段
    closeAll() {
      if (!this.scheduleData[this.selectedDate]) {
        this.$set(this.scheduleData, this.selectedDate, {});
      }
      this.timeSlots.forEach(slot => {
        this.$set(this.scheduleData[this.selectedDate], slot.time, 0);
      });
    },

    // 开启全部时间段
    openAll() {
      if (!this.scheduleData[this.selectedDate]) {
        this.$set(this.scheduleData, this.selectedDate, {});
      }
      this.timeSlots.forEach(slot => {
        this.$set(this.scheduleData[this.selectedDate], slot.time, 1);
      });
    },

    // 7天相同设置
    copySetting() {
      const currentDaySchedule = this.scheduleData[this.selectedDate] || {};
      this.weekDays.forEach(day => {
        this.$set(this.scheduleData, day.date, { ...currentDaySchedule });
      });
      this.$message.success('已应用到本周所有日期');
    },

    // 加载排班数据
    loadScheduleData() {
      console.log('加载技师排班数据, staffId:', this.staffInfo.id);
      
      getStaffSchedule({ staffId: this.staffInfo.id }).then(response => {
        console.log('API响应:', response);
        
        if (response && typeof response === 'string') {
          // 解析84字符的排班字符串
          this.scheduleData = this.parseScheduleString(response);
        } else if (response && response.data) {
          // 如果返回的是对象格式，直接使用
          console.log('使用对象格式的排班数据');
          this.scheduleData = response.data;
        } else {
          console.log('无排班数据，初始化为空');
          this.scheduleData = {};
        }
      }).catch(err => {
        console.error('加载排班数据失败:', err);
        this.$message.warning('加载排班数据失败，将使用默认设置');
        this.scheduleData = {};
      });
    },

    // 保存排班
    saveSchedule() {
      this.saving = true;
      
      try {
        // 转换为84字符的排班字符串
        const scheduleString = this.convertToScheduleString(this.scheduleData);
        
        console.log('转换后的排班字符串:', scheduleString);
        console.log('字符串长度:', scheduleString.length);
        console.log('字符串是否为空:', !scheduleString);
        
        if (!scheduleString || scheduleString.length === 0) {
          this.$message.error('排班数据为空，请先设置排班时间');
          this.saving = false;
          return;
        }
        
        const data = {
          staffId: this.staffInfo.id,
          scheduleTime: scheduleString
        };
        
        console.log('保存排班数据 - 最终发送:', data);
        console.log('参数验证 - staffId:', data.staffId);
        console.log('参数验证 - scheduleTime:', data.scheduleTime);
        
        saveStaffSchedule(data).then(() => {
          this.$message.success('排班保存成功');
          this.open_modal = false;
          this.$emit('scheduleUpdated');
        }).catch(err => {
          console.error('保存排班失败:', err);
          this.$message.error(err.msg || '保存失败');
        }).finally(() => {
          this.saving = false;
        });
      } catch (error) {
        console.error('排班数据处理失败:', error);
        this.$message.error('数据处理失败，请检查输入');
        this.saving = false;
      }
    },

    // 格式化日期
    formatDate(date) {
      return date.toISOString().split('T')[0];
    }
  }
};
</script>

<style scoped>
.scheduling-container {
  padding: 20px;
}

.staff-info {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 20px;
}

.date-section {
  margin-bottom: 30px;
  
  h3 {
    color: #333;

    padding: 10px 20px;
    margin: 0 0 15px 0;
    border-radius: 8px;
    text-align: center;
  }
}

.week-selector {
  display: flex;
  gap: 10px;
  justify-content: center;
  
  .day-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 15px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    background: #fff;
    transition: all 0.3s;
    
    &.active {
      background: #e91e63;
      color: #fff;
      border-color: #e91e63;
    }
    
    .day-name {
      font-size: 12px;
      margin-bottom: 5px;
    }
    
    .day-number {
      font-size: 18px;
      font-weight: bold;
    }
  }
}

.time-section {
  margin-bottom: 30px;
  
  h3 {
    color: #333;
    margin-bottom: 15px;
    padding-left: 10px;
    border-left: 3px solid #e91e63;
  }
}

.time-slots {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  
  .time-slot {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s;
    
    &.available {
      background: #2196f3;
      color: #fff;
    }
    
    &.busy {
      background: #f44336;
      color: #fff;
    }
    
    &.past-slot {
      background: #e0e0e0 !important;
      color: #9e9e9e !important;
      cursor: not-allowed !important;
      opacity: 0.6;
      
      &:hover {
        background: #e0e0e0 !important;
        transform: none !important;
      }
    }
    
    .time {
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 2px;
    }
    
    .status {
      font-size: 12px;
    }
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 30px;
}

.save-section {
  text-align: center;
  padding: 20px 0;
  border-top: 1px solid #eee;
  
  .el-button {
    width: 200px;
    height: 50px;
    font-size: 16px;
    border-radius: 25px;
    background: #e91e63;
    border-color: #e91e63;
  }
}
</style>
